import React, { ElementType, ReactNode } from "react";
import { useRender } from "@base-ui-components/react/use-render";
import { mergeProps } from "@base-ui-components/react/merge-props";
import {
  badgeRootClasses,
  badgeClasses,
  type BadgeVariants,
} from "./Badge.style";

export type BadgeProps = useRender.ComponentProps<"span"> &
  BadgeVariants & {
    children: ReactNode;
    className?: string;
    anchorOrigin?: "horizontal" | "vertical";
    disableOutline?: boolean;
    size?: "small" | "medium" | "large";
    shape?: "circle" | "square";
    placement?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
    color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
    badgeContent?: ReactNode;
    component?: ElementType;
    showZero?: boolean;
    max?: number;
  };

const Badge = (props: BadgeProps) => {
  const {
    render,
    children,
    anchorOrigin = "horizontal",
    size = "medium",
    shape = "circle",
    placement = "top-right",
    color = "primary",
    badgeContent,
    component,
    className,
    showZero = false,
    max = 99,
    ...rest
  } = props;

  const defaultElementType = component || "span";
  const element = render ?? React.createElement(defaultElementType);

  const getBadgeContent = () => {
    if (badgeContent === undefined || badgeContent === null) {
      return null;
    }

    const numericContent = Number(badgeContent);
    if (isNaN(numericContent)) {
      return badgeContent;
    }

    if (numericContent === 0 && !showZero) {
      return null;
    }

    return numericContent > max ? `${max}+` : numericContent;
  };

  const displayContent = getBadgeContent();

  const defaultProps: useRender.ElementProps<"span"> = {
    className: badgeRootClasses({ className }),
    children: (
      <>
        {children}
        {displayContent !== null && (
          <span
            className={badgeClasses({
              anchorOrigin,
              size,
              shape,
              placement,
              color,
            })}
          >
            {displayContent}
          </span>
        )}
      </>
    ),
  };

  const renderElement = useRender({
    render: element,
    props: mergeProps<"span">(defaultProps, rest),
  });

  return renderElement;
};

export default Badge;
